import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import '../styles/components/TsumugiChat.css';
import '../styles/pages/TryDemoPage.css';

/**
 * TryDemoPage - つむぎ寄り添い体験ページ（chat風UI）
 *
 * コンセプト：
 * - 本番のchatと同じUI体験
 * - API通信なし、固定レスポンス
 * - 「集めた声」フレームでつむぎの voice を表現（「うん。」「〜って、聞こえてきたよ」）
 *
 * フロー:
 *   5択 → 2段応答(turn1=集めた声 / turn2=身体感覚) → 2択分岐
 *     ├ 「もうちょっとだけ、いる」 → 機能ツアー(ごはん・検査) → CTA
 *     └ 「今日はここまでにする」 → 別れの1ターン → CTA
 */
const TryDemoPage = () => {
  const messagesEndRef = useRef(null);
  const hasInteracted = useRef(false);

  useEffect(() => {
    document.title = 'つむぎ - 体験版';
  }, []);

  const initialOptions = [
    { id: 'tired', text: '😴 今日も疲れて何もできなかった' },
    { id: 'misunderstood', text: '😢 誰にもわかってもらえない' },
    { id: 'confused', text: '🤔 何を食べればいいかわからない' },
    { id: 'newly_diagnosed', text: '😰 最近診断されて不安' },
    { id: 'just_visited', text: '🌱 特にない、ただ来てみた' }
  ];

  const [messages, setMessages] = useState([
    {
      role: 'tsumugi',
      content: 'こんにちは。つむぎだよ。\n\n来てくれてありがとう。\n今のあなたに、近いのある?'
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [currentOptions, setCurrentOptions] = useState(initialOptions);
  const [showCTA, setShowCTA] = useState(false);
  const [showBranchOptions, setShowBranchOptions] = useState(false);

  // 2択分岐の選択肢
  const branchOptions = [
    { id: 'tour', text: 'もうちょっとだけ、いる' },
    { id: 'end', text: '今日はここまでにする' }
  ];

  // スクロールを最下部に
  const scrollToBottom = () => {
    if (hasInteracted.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // つむぎの固定応答（2段構造: turn1 = 集めた声の受信 / turn2 = 身体感覚の代弁）
  const getResponse = (optionId) => {
    const responses = {
      tired: {
        turn1: 'うん。「もう、起きるだけで力使った」って、聞こえてきたよ。',
        turn2: '布団が、いつもより重い朝があるんだよね。\nそれは怠けじゃないんだ。'
      },
      misunderstood: {
        turn1: 'うん。見えない病気って、いちばん独りになりやすいんだ。',
        turn2: '「気のせい」ってことばに、何度も削られた夜があるって、聞こえてきたよ。'
      },
      confused: {
        turn1: 'うん。スーパーの棚の前で、立ち尽くす夜があるんだよね。',
        turn2: '「食べちゃダメなもの」を数えると、料理がこわくなる。\n全部を一度に変えなくていいんだ。'
      },
      newly_diagnosed: {
        turn1: 'うん。診察室を出たあと、世界の音が遠くなる感じ、あるよね。',
        turn2: '検索の窓に、夜ごとことばを打ち込んでた人たちの話が、つむぎにも届いてる。\n今夜は、まだ何も決めなくていい。'
      },
      just_visited: {
        turn1: 'うん。それでもいいよ。',
        turn2: 'つむぎは、来てくれただけで嬉しいんだ。'
      }
    };
    return responses[optionId];
  };

  // オプション選択時の処理
  const handleOptionSelect = async (option) => {
    hasInteracted.current = true;

    // ユーザーメッセージを追加
    setMessages(prev => [...prev, { role: 'user', content: option.text }]);
    setCurrentOptions([]);

    // タイピング中表示
    setIsTyping(true);

    const response = getResponse(option.id);

    // 段階的に応答を表示: turn1（集めた声の受信） → turn2（身体感覚の代弁）
    await new Promise(resolve => setTimeout(resolve, 800));
    setIsTyping(false);
    setMessages(prev => [...prev, { role: 'tsumugi', content: response.turn1 }]);

    await new Promise(resolve => setTimeout(resolve, 600));
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    setIsTyping(false);
    setMessages(prev => [...prev, { role: 'tsumugi', content: response.turn2 }]);

    // 2択分岐を表示（緩衝メッセージは廃止：間が会話の自然さを作る）
    await new Promise(resolve => setTimeout(resolve, 600));
    setShowBranchOptions(true);
  };

  // 機能ツアー（伝聞構文。「集めた声」フレームで機能を紹介する）
  const runFeatureTour = async () => {
    // ツアー1: ごはんの話
    await new Promise(resolve => setTimeout(resolve, 400));
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    setIsTyping(false);
    setMessages(prev => [...prev, { role: 'tsumugi', content: 'ごはんの話も、つむぎは聞くよ。' }]);

    await new Promise(resolve => setTimeout(resolve, 600));
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    setIsTyping(false);
    setMessages(prev => [...prev, { role: 'tsumugi', content: '昨日も「今夜何食べよう」って来た人いたよ。\n一緒に考えた。' }]);

    // ツアー2: 検査結果の話
    await new Promise(resolve => setTimeout(resolve, 700));
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    setIsTyping(false);
    setMessages(prev => [...prev, { role: 'tsumugi', content: '病院でもらった紙、ひとりで読まなくていいからね。' }]);

    await new Promise(resolve => setTimeout(resolve, 600));
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    setIsTyping(false);
    setMessages(prev => [...prev, { role: 'tsumugi', content: 'TSHの数値、隣で一緒に読む夜もあるよ。' }]);

    // ツアー完了後CTA
    await new Promise(resolve => setTimeout(resolve, 700));
    setShowCTA(true);
  };

  // 2択分岐の選択処理
  const handleBranchSelect = async (option) => {
    // ユーザーの選択を吹き出しとして表示
    setMessages(prev => [...prev, { role: 'user', content: option.text }]);
    setShowBranchOptions(false);

    if (option.id === 'tour') {
      await runFeatureTour();
    } else {
      // 「今日はここまで」を選んだら、別れの1ターンを挟んでからCTA
      await new Promise(resolve => setTimeout(resolve, 500));
      setIsTyping(true);
      await new Promise(resolve => setTimeout(resolve, 800));
      setIsTyping(false);
      setMessages(prev => [...prev, { role: 'tsumugi', content: 'うん、おやすみ。\nまた話しに来てね。' }]);

      await new Promise(resolve => setTimeout(resolve, 800));
      setShowCTA(true);
    }
  };

  // デモをリセット
  const resetDemo = () => {
    hasInteracted.current = false;

    setMessages([
      {
        role: 'tsumugi',
        content: 'こんにちは。つむぎだよ。\n\n来てくれてありがとう。\n今のあなたに、近いのある?'
      }
    ]);
    setCurrentOptions(initialOptions);
    setShowCTA(false);
    setShowBranchOptions(false);
    setIsTyping(false);
    window.scrollTo(0, 0);
  };

  return (
    <AppLayout>
      <div className="try-demo-chat">
        {/* デモバナー */}
        <div className="try-demo-chat__banner">
          <span className="try-demo-chat__banner-icon">✨</span>
          <span>お試しモード・登録なしで会えるよ</span>
        </div>

        {/* メッセージエリア */}
        <div className="tsumugi-messages try-demo-chat__messages">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`tsumugi-message ${msg.role === 'tsumugi' ? 'tsumugi' : 'user'}`}
            >
              {msg.role === 'tsumugi' && (
                <div className="message-avatar">
                  <img src="/tsumugi.png" alt="つむぎ" />
                </div>
              )}
              <div className="message-bubble">
                <p className="message-content" style={{ whiteSpace: 'pre-line' }}>
                  {msg.content}
                </p>
              </div>
              {msg.role === 'user' && (
                <div className="message-avatar user">
                  <span className="emoji">👤</span>
                </div>
              )}
            </div>
          ))}

          {/* タイピングインジケーター */}
          {isTyping && (
            <div className="tsumugi-message tsumugi typing">
              <div className="message-avatar">
                <img src="/tsumugi.png" alt="つむぎ" />
              </div>
              <div className="message-bubble">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          {/* 選択肢 */}
          {currentOptions.length > 0 && !isTyping && (
            <div className="try-demo-chat__options">
              {currentOptions.map((option) => (
                <button
                  key={option.id}
                  className="try-demo-chat__option"
                  onClick={() => handleOptionSelect(option)}
                >
                  {option.text}
                </button>
              ))}
            </div>
          )}

          {/* 2択分岐: もう少し見る / 今日はここまで */}
          {showBranchOptions && !isTyping && (
            <div className="try-demo-chat__options">
              {branchOptions.map((option) => (
                <button
                  key={option.id}
                  className="try-demo-chat__option"
                  onClick={() => handleBranchSelect(option)}
                >
                  {option.text}
                </button>
              ))}
            </div>
          )}

          {/* CTA */}
          {showCTA && (
            <div className="try-demo-chat__cta">
              <div className="try-demo-chat__cta-content">
                <p className="try-demo-chat__cta-title">
                  つむぎは、ここにいるよ。
                </p>
                <p className="try-demo-chat__cta-subtitle" style={{ whiteSpace: 'pre-line' }}>
                  {'急がなくていいよ。\nつむぎは、消えないから。'}
                </p>

                <Link to="/register" className="try-demo-chat__cta-button">
                  つむぎと話し続ける
                </Link>
                <p className="try-demo-chat__cta-note">
                  月50ポイント無料・クレカ不要
                </p>

                <button
                  onClick={resetDemo}
                  className="try-demo-chat__restart"
                >
                  もう一度、最初から話す
                </button>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>
    </AppLayout>
  );
};

export default TryDemoPage;
